import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, X, Upload, Download, CheckCircle2, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { api, API_BASE_URL, tokenStore } from '@/api/client';

/**
 * Status updates from a spreadsheet — one row per shipment, each with its own status and date.
 *
 * Two steps on purpose, and the first one writes nothing: the file is CHECKED and every row is shown
 * with whatever is wrong with it before anything is applied. A status update is not reversible by
 * pressing undo — a wrong code on row 40 of 300 moves 300 real shipments — so the operator sees the
 * damage before they cause it, not after.
 *
 * Invalid rows do not hold back the valid ones: Apply sends the file again and the server applies
 * exactly the rows that passed, reporting the rest untouched.
 */
type Row = {
  row: number; number: string; statusInput: string; dateInput: string;
  statusName?: string; orderNo?: string; orderType?: string; merchant?: string;
  currentStatus?: string; error?: string; applied?: boolean;
};
type Result = { dryRun: boolean; total: number; valid: number; invalid: number; applied?: number; failed?: number; rows: Row[] };

export function BulkStatusUploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [applied, setApplied] = useState(false);
  // Whether THIS login may record Delivered / RTO Delivered from a sheet without a POD (admin-granted).
  const { data: waiver } = useQuery({
    queryKey: ['pod-waiver-me'],
    enabled: open,
    queryFn: async () => (await api.get('/tracking/pod-waiver/me')).data as { allowed: boolean },
  });

  const send = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const fd = new FormData();
      fd.append('file', file!);
      fd.append('dryRun', dryRun ? '1' : '0');
      return (await api.post('/tracking/bulk-status', fd, { timeout: 180_000 })).data as Result;
    },
    onSuccess: (r, dryRun) => {
      setResult(r);
      if (!dryRun) {
        setApplied(true);
        if (r.applied) toast.success(`${r.applied} shipment(s) updated`);
        if (r.failed) toast.warning(`${r.failed} row(s) were not applied`);
        qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts') });
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not read that file'),
  });

  // Streamed through the API so the browser SAVES it with its auth header attached.
  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tracking/bulk-status/template`, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      if (!res.ok) throw new Error(String(res.status));
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url; a.download = 'bulk-status-update-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);   // revoking in the same tick cancels the save
    } catch { toast.error('Could not download the template'); }
  };

  const reset = () => { setFile(null); setResult(null); setApplied(false); if (fileRef.current) fileRef.current.value = ''; };
  const close = () => { reset(); onClose(); };
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><FileSpreadsheet size={17} /> Bulk status update from Excel</h3>
            <p className="text-xs text-slate-500">One row per shipment: AWB / LR number, status code and date</p>
            <p className={`text-xs mt-0.5 ${waiver?.allowed ? 'text-amber-700' : 'text-slate-500'}`}>
              {waiver?.allowed
                ? 'You may record Delivered / RTO Delivered here without a POD (marked "POD waived").'
                : 'Delivered / RTO Delivered need a POD — record those from Scan, unless an admin allows "Bulk status without POD" for you.'}
            </p>
          </div>
          <button onClick={close} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={downloadTemplate}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm inline-flex items-center gap-1.5 hover:bg-slate-50">
              <Download size={14} /> Download template
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setApplied(false); }} />
            <button onClick={() => fileRef.current?.click()}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm inline-flex items-center gap-1.5 hover:bg-slate-50">
              <Upload size={14} /> {file ? 'Choose another file' : 'Choose file'}
            </button>
            {file && <span className="text-xs text-slate-600 truncate max-w-[14rem]" title={file.name}>{file.name}</span>}
            <button onClick={() => send.mutate(true)} disabled={!file || send.isPending}
              className="ml-auto px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              {send.isPending && send.variables === true ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Check file
            </button>
          </div>

          <div className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            The template's <b>Status Codes</b> sheet lists every code you can use. The date may be
            <b> today or any past date</b>; a future date is refused. Nothing is changed until you press
            Apply, and checking the file changes nothing at all.
          </div>

          {result && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Tile label="Rows in file" value={result.total} tone="text-slate-900" />
                <Tile label={applied ? 'Updated' : 'Ready to apply'} value={applied ? (result.applied ?? 0) : result.valid} tone="text-emerald-700" />
                <Tile label={applied ? 'Not applied' : 'With a problem'} value={applied ? (result.failed ?? 0) : result.invalid} tone="text-rose-700" />
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[42vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-xs text-slate-600">
                      <th className="px-3 py-2 w-12">Row</th>
                      <th className="px-3 py-2">AWB / LR</th>
                      <th className="px-3 py-2">Shipment</th>
                      <th className="px-3 py-2">Now</th>
                      <th className="px-3 py-2">New status</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={r.row} className={`border-t border-slate-100 ${r.error ? 'bg-rose-50/50' : ''}`}>
                        <td className="px-3 py-2 text-slate-400">{r.row}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.number || '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.orderNo ? <>{r.orderNo}<span className="text-slate-400"> · {r.orderType}</span><div className="text-slate-500">{r.merchant}</div></> : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.currentStatus ?? '—'}</td>
                        <td className="px-3 py-2 text-xs">{r.statusName ?? <span className="text-slate-400">{r.statusInput || '—'}</span>}</td>
                        <td className="px-3 py-2 text-xs font-mono">{r.dateInput || '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.error
                            ? <span className="text-rose-700 inline-flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{r.error}</span>
                            : applied
                              ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Updated</span>
                              : <span className="text-emerald-700">OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-100">
          <button onClick={close} className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">
            {applied ? 'Close' : 'Cancel'}
          </button>
          {/* Apply is offered only after a check, and only when something actually passed it. */}
          {!applied && (
            <button onClick={() => send.mutate(false)} disabled={!result || !result.valid || send.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              {send.isPending && send.variables === false ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Apply {result?.valid ? `${result.valid} row(s)` : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2.5">
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      <div className="text-[11px] text-slate-600 leading-tight">{label}</div>
    </div>
  );
}
